import html2canvas from 'html2canvas';
import { useGameStore } from '~/stores/games.store';
import { useAuthStore } from '~/stores/auth.store';
import { socialMeta } from 'assets/data/config';

type TCaptureOptions = {
  backgroundColor?: string;
  fileName?: string;
  scale?: number;
  quality?: number;
  format?: 'png' | 'jpeg';
};

type TShareOptions = {
  title?: string;
  text?: string;
};

export default () => {
  const { $t, $trackEvent } = useNuxtApp();
  const route = useRoute();
  const game = useGameStore();
  const auth = useAuthStore();
  const site = useSiteStore();

  const defaultShareUrl = computed(() => {
    const url = new URL('/share', window.location.origin);
    url.searchParams.set('path', route.path);
    return url;
  });

  const canShareNative = computed<boolean>(
    () => !!navigator?.canShare || !!navigator?.share,
  );

  const canShareFiles = computed<boolean>(() => {
    if (!canShareNative.value) return false;
    try {
      const probe = new File([''], 'probe.txt', { type: 'text/plain' });
      return navigator.canShare({ files: [probe] });
    } catch {
      return false;
    }
  });

  const ensureFileName = (name: string | undefined, format: 'png' | 'jpeg') => {
    const fallback = `capture.${format === 'jpeg' ? 'jpg' : 'png'}`;
    if (!name) return fallback;
    const ext = format === 'jpeg' ? '.jpg' : '.png';
    return name.toLowerCase().endsWith(ext) ? name : `${name}${ext}`;
  };

  const safeOptions = (options?: TCaptureOptions) => {
    return {
      backgroundColor: options?.backgroundColor || null,
      scale: options?.scale ?? (window.devicePixelRatio || 2),
      useCORS: true,
      logging: false,
    };
  };

  const captureElementToBlob = async (
    el: HTMLElement,
    options?: TCaptureOptions,
  ): Promise<Blob> => {
    await nextTick();
    const canvas = await html2canvas(el, safeOptions(options));
    const format = options?.format ?? 'png';
    const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
    const quality = options?.quality ?? 0.92;

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to render element to image.'));
            return;
          }
          resolve(blob);
        },
        mimeType,
        quality,
      );
    });
  };

  const shareBlob = async (
    blob: Blob,
    fileName?: string,
    shareOptions?: TShareOptions,
  ) => {
    if (!!blob) {
      const format = blob.type === 'image/jpeg' ? 'jpeg' : 'png';
      const safeName = ensureFileName(fileName, format);

      const file = new File([blob], safeName, {
        type: blob.type || 'image/png',
      });

      if (canShareFiles.value) {
        await navigator.share({
          files: [file],
          title: shareOptions?.title,
          text: shareOptions?.text,
        });
        trackShare('file');
        return;
      }
    }

    // fallback if file share not enabled
    if (canShareNative.value && (shareOptions?.title || shareOptions?.text)) {
      await navigator.share({
        title: shareOptions?.title,
        text: shareOptions?.text,
      });
      trackShare('native');
      return;
    }
  };

  const shareElementAsImageFile = async (
    el: HTMLElement,
    options?: TCaptureOptions,
    shareOptions?: TShareOptions,
  ) => {
    let blob: Blob | null = null;
    if (!!el) blob = await captureElementToBlob(el, options);
    await shareBlob(blob, options?.fileName, shareOptions);
  };

  const emailShare = (shareText: string, shareUrl?: string) => {
    const url = !!shareUrl ? shareUrl : defaultShareUrl.value;
    window.open(
      `mailto:?subject=${$t(`email-${shareText}`)}${encodeURIComponent(url.toString())}`,
    );
    trackShare('email');
  };

  const whatsappShare = (shareText: string, shareUrl?: string) => {
    const url = !!shareUrl ? shareUrl : defaultShareUrl.value.toString();
    const whatsappLink = `https://wa.me?text=${encodeURIComponent($t(`whatsapp-${shareText}`))}%0a${encodeURIComponent(url.toString())}`;
    window.open(whatsappLink, '_blank');
    trackShare('whatsapp');
  };

  const facebookShare = (shareUrl?: string) => {
    const href = !!shareUrl ? shareUrl : defaultShareUrl.value.toString();
    window.FB.ui({
      method: 'share',
      display: 'popup',
      href,
    });
    trackShare('facebook');
  };

  const twitterShare = (shareText: string, shareUrl?: string) => {
    const url = encodeURIComponent(
      !!shareUrl ? shareUrl : defaultShareUrl.value.toString(),
    );
    const text = $t(`twitter-${shareText}`);
    const related = socialMeta[site.getRegionCode]?.related;
    const actionUrl = new URL('/intent/tweet', 'https://twitter.com');
    actionUrl.searchParams.set('text', text);
    actionUrl.searchParams.set('related', related);
    actionUrl.searchParams.set('url', url);
    window.open(actionUrl.toString(), '_blank');
    trackShare('twitter');
  };

  const nativeShare = async ({
    title,
    text,
    url,
  }: {
    title?: string;
    text?: string;
    url?: URL;
  }) => {
    try {
      if (canShareNative.value)
        await navigator.share({
          title: title || $t('default-share-title'),
          text: text || $t('default-share-text'),
          url: url?.toString() || defaultShareUrl.value.toString(),
        });
    } catch (e) {
      console.error('Error sharing:', e);
    }
  };

  const trackShare = (
    channel: 'whatsapp' | 'twitter' | 'facebook' | 'email' | 'file' | 'native',
  ) => {
    const inGame = !!game?.currentGame;
    $trackEvent('GameEvent', 'SOCIAL_SHARE', {
      ...(inGame && {
        game_name: game?.currentGame?.name,
        game_id: game?.currentGame?.id,
        game_alias: game?.currentGame?.alias,
      }),
      msisdn: auth?.currentUser?.username,
      path: route.path,
      channel,
    });
  };

  return {
    canShareNative,
    canShareFiles,
    emailShare,
    facebookShare,
    nativeShare,
    shareElementAsImageFile,
    whatsappShare,
    twitterShare,
  };
};
